/**
 * API工具函数
 * 包含请求处理、环境检测等通用功能
 */

import Logger from "../utils/logger";
import axiosInstance from "../services/axiosConfig";
import { API_PATHS, RETRY_CONFIG, STORAGE_KEYS, RATE_LIMIT_CONFIG } from "./config";

/**
 * 判断当前环境是否为开发环境
 * @returns {boolean} 是否为开发环境
 */
export const isDevelopment = () => {
  return (
    process.env.NODE_ENV === "development" ||
    window.location.hostname === "localhost"
  );
};

/**
 * 延迟函数
 * @param {number} ms 延迟毫秒数
 * @returns {Promise} 延迟Promise
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 计算重试延迟时间（指数退避）
 * @param {number} attempt 重试次数
 * @returns {number} 延迟毫秒数
 */
export const calculateRetryDelay = (attempt) => {
  const delay =
    RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, attempt);
  return Math.min(delay, RETRY_CONFIG.maxDelay);
};

/**
 * 获取当前登录用户
 * @returns {Object|null} 用户数据或null
 */
export const getCurrentUser = () => {
  const userData = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
  if (userData === "undefined") {
    return null;
  }
  return userData ? JSON.parse(userData) : null;
};

/**
 * 保存当前用户数据
 * @param {Object} user 用户数据
 */
export const saveCurrentUser = (user) => {
  localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
};

// 内存级别的定制客户 slug（页面生命周期内有效，不持久化）
let _activeCustomClientSlug = null;

/**
 * 设置当前激活的定制客户 slug
 * 由 ClientPage 在挂载/卸载时调用
 * @param {string|null} slug
 */
export const setCustomClientSlug = (slug) => {
  _activeCustomClientSlug = slug || null;
};

/**
 * 获取当前激活的定制客户 slug（供各 stream 函数透明注入）
 * @returns {string|null}
 */
export const getCustomClientSlug = () => _activeCustomClientSlug;

/**
 * 从非流式模型响应中提取文本内容
 * @param {Object} payload - 后端返回的模型数据
 * @returns {string} 提取后的文本
 */
export const extractModelResponseText = (payload) => {
  const modelData =
    payload?.choices
      ? payload
      : payload?.data?.choices
        ? payload.data
        : payload;

  const choices = Array.isArray(modelData?.choices) ? modelData.choices : [];
  if (choices.length === 0) {
    return "";
  }

  const firstChoice = choices[0] || {};
  const content = firstChoice?.message?.content ?? firstChoice?.text ?? modelData?.content;

  if (typeof content === "string") {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item?.type === "text" && typeof item?.text === "string") {
          return item.text;
        }
        return "";
      })
      .join("");
  }

  return "";
};

/**
 * 调用统一的非流式模型接口
 * @param {Object} requestBody - 模型请求体
 * @param {Object} options - 可选请求配置
 * @returns {Promise<{fullResponse: string, rawData: Object}>}
 */
export const requestModelNonStream = async (requestBody, options = {}) => {
  // AI 生成可能经历多个渠道超时后重试；前端不能先于后端重试链断开。
  const { timeout = 1800000 } = options;
  const response = await axiosInstance.post(
    `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    {
      ...requestBody,
      stream: false,
    },
    { timeout }
  );

  if (!response?.success) {
    throw new Error(response?.message || "AI请求失败");
  }

  const fullResponse = extractModelResponseText(response.data);
  if (!fullResponse || !fullResponse.trim()) {
    throw new Error("模型返回空响应，请稍后重试");
  }

  return {
    fullResponse,
    rawData: response.data,
    chargeSession: response.data?.charge_session || null,
  };
};

/**
 * 带重试机制的API请求函数
 * @param {string} url 请求URL
 * @param {object} options 请求选项
 * @param {number} attempt 当前重试次数
 * @returns {Promise<Response>} 响应对象
 */
export const fetchWithRetry = async (url, options, attempt = 0) => {
  try {
    // 将fetch选项转换为axios选项
    const axiosOptions = {
      method: options.method || "GET",
      url: url,
      headers: options.headers || {},
      data: options.body ? JSON.parse(options.body) : null,
      timeout: options.timeout || 60000,
    };

    // 使用axios进行请求, 响应已经被拦截器处理，直接返回即可
    return await axiosInstance(axiosOptions);
  } catch (error) {
    // 处理HTTP错误状态
    if (error.success === false) {
      // 如果是429错误且还有重试次数，则重试
      if (error.status === 429 && attempt < RETRY_CONFIG.maxRetries) {
        const retryDelay = calculateRetryDelay(attempt);
        Logger.warn(
          `API请求被限流(429)，将在${retryDelay}ms后重试 (第${
            attempt + 1
          }次重试)`,
          { url, attempt }
        );

        await delay(retryDelay);
        return fetchWithRetry(url, options, attempt + 1);
      }

      // 对于其他已经格式化为 { success: false } 的业务错误，直接返回
      return error;
    }

    // 网络错误等其他可重试的错误
    if (
      attempt < RETRY_CONFIG.maxRetries &&
      !error.message.includes("API密钥") &&
      !error.message.includes("请求过于频繁")
    ) {
      const retryDelay = calculateRetryDelay(attempt);
      Logger.warn(
        `网络请求失败，将在${retryDelay}ms后重试 (第${attempt + 1}次重试)`,
        { url, attempt, error: error.message }
      );

      await delay(retryDelay);
      return fetchWithRetry(url, options, attempt + 1);
    }

    // 对于不可重试或达到重试上限的错误，包装成统一格式返回
    return {
      success: false,
      message: error.message || "请求失败",
      data: null,
    };
  }
};

/**
 * 处理axios流式响应的辅助函数
 * @param {string} responseText - axios流式响应返回的文本
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @returns {Promise<string>} 完整的响应文本
 */
export const handleStreamResponse = async (responseText, onChunkReceived) => {
  let fullResponse = "";

  // 对于axios，我们直接接收文本而非流
  // 按行分割文本内容
  const lines = responseText.split("\n");

  for (const line of lines) {
    if (line.trim() === "" || !line.startsWith("data: ")) {
      continue;
    }

    const jsonData = line.slice(6);
    if (jsonData.trim() === "[DONE]") {
      break;
    }

    try {
      const parsed = JSON.parse(jsonData);
      const content = parsed.choices?.[0]?.delta?.content;
      if (content) {
        fullResponse += content;
        if (onChunkReceived) {
          onChunkReceived(content);
        }
      }
    } catch (parseError) {
      Logger.warn("解析流式响应JSON时出错", {
        error: parseError.message,
        data: jsonData,
      });
    }
  }

  return fullResponse;
};

const extractStreamContent = (parsed) => {
  const content = parsed?.choices?.[0]?.delta?.content;
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content)) {
    return content
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.type === "text" && typeof item?.text === "string") {
          return item.text;
        }
        return "";
      })
      .join("");
  }
  return "";
};

const processStreamLine = (line, onChunkReceived) => {
  if (!line.trim() || !line.startsWith("data: ")) {
    return { done: false, content: "" };
  }

  const jsonData = line.slice(6);
  if (jsonData.trim() === "[DONE]") {
    return { done: true, content: "" };
  }

  try {
    const parsed = JSON.parse(jsonData);
    const content = extractStreamContent(parsed);
    if (content && onChunkReceived) {
      onChunkReceived(content);
    }
    return { done: false, content };
  } catch (parseError) {
    Logger.warn("解析流式响应JSON时出错", {
      error: parseError.message,
      data: jsonData,
    });
    return { done: false, content: "" };
  }
};

/**
 * 获取指定模式是否启用流式输出。失败时保持非流式，避免影响既有生成链路。
 * @param {string} mode 模式标识
 * @returns {Promise<boolean>}
 */
export const getModeStreamPreference = async (mode) => {
  if (!mode) return false;

  try {
    const response = await axiosInstance.get(
      `${API_PATHS.BASE_API_URL}${API_PATHS.MODE.GET_PUBLIC_CONFIGS}`
    );
    const configs = response?.success && Array.isArray(response.data)
      ? response.data
      : [];
    const modeConfig = configs.find((item) => item.key === mode);
    return modeConfig?.stream === true;
  } catch (error) {
    Logger.warn("获取模式流式配置失败，使用非流式请求", {
      mode,
      error: error.message,
    });
    return false;
  }
};

/**
 * 调用统一的流式模型接口。
 * @param {Object} requestBody - 模型请求体
 * @param {function} onChunkReceived - 接收到文本块时的回调
 * @param {Object} options - 可选请求配置
 * @returns {Promise<{fullResponse: string, rawData: string, chargeSession: null}>}
 */
export const requestModelStream = async (
  requestBody,
  onChunkReceived,
  options = {}
) => {
  const { timeout = 1800000 } = options;
  const currentUser = getCurrentUser();
  const token = currentUser?.token;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  let response;
  try {
    response = await fetch(
      `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL_STREAM}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ...requestBody,
          userId: requestBody.userId || currentUser?.id || 0,
          stream: true,
        }),
        signal: controller.signal,
      }
    );
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("模型流式请求超时，请稍后重试");
    }
    throw error;
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    let errorMessage = `模型流式请求失败 (${response.status})`;
    try {
      const errorPayload = await response.json();
      errorMessage = errorPayload?.message || errorMessage;
    } catch (_) {
      // ignore non-JSON error body
    }
    throw new Error(errorMessage);
  }

  if (!response.body) {
    clearTimeout(timeoutId);
    const responseText = await response.text();
    const fullText = await handleStreamResponse(responseText, onChunkReceived);
    if (fullText.startsWith("错误:")) {
      throw new Error(fullText.replace(/^错误:\s*/, ""));
    }
    return {
      fullResponse: fullText,
      rawData: responseText,
      chargeSession: null,
    };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let fullResponse = "";
  let rawData = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunkText = decoder.decode(value, { stream: true });
      rawData += chunkText;
      buffer += chunkText;

      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const result = processStreamLine(line, onChunkReceived);
        fullResponse += result.content;
        if (result.done) {
          clearTimeout(timeoutId);
          if (fullResponse.startsWith("错误:")) {
            throw new Error(fullResponse.replace(/^错误:\s*/, ""));
          }
          return {
            fullResponse,
            rawData,
            chargeSession: null,
          };
        }
      }
    }

    if (buffer) {
      const result = processStreamLine(buffer, onChunkReceived);
      fullResponse += result.content;
    }
  } finally {
    clearTimeout(timeoutId);
    reader.releaseLock();
  }

  if (fullResponse.startsWith("错误:")) {
    throw new Error(fullResponse.replace(/^错误:\s*/, ""));
  }
  if (!fullResponse || !fullResponse.trim()) {
    throw new Error("模型返回空响应，请稍后重试");
  }

  return {
    fullResponse,
    rawData,
    chargeSession: null,
  };
};

/**
 * 按后台模式配置自动选择流式或非流式模型接口。
 * @param {Object} requestBody - 模型请求体
 * @param {function} onChunkReceived - 接收到文本块时的回调
 * @param {Object} options - 可选请求配置
 */
export const cleanErrorMessage = (errorMsg) => {
  if (!errorMsg) return "系统繁忙";
  const msgStr = String(errorMsg);

  const sensitivePatterns = [
    /渠道/i,
    /PRO站/i,
    /主站/i,
    /冷锋/i,
    /小新API/i,
    /gemini/i,
    /gpt/i,
    /claude/i,
    /deepseek/i,
    /qwen/i,
    /openai/i,
    /请求异常/i,
    /API服务器/i,
    /接口/i,
    /503/i,
    /502/i,
    /504/i,
    /403/i,
    /401/i,
    /500/i,
    /详情/i,
    /timeout/i,
    /超时/i,
    /Failed to fetch/i,
    /AxiosError/i,
    /Network Error/i
  ];

  if (sensitivePatterns.some((pattern) => pattern.test(msgStr))) {
    return "系统繁忙";
  }
  return errorMsg;
};

export const requestModelByModeConfig = async (
  requestBody,
  onChunkReceived,
  options = {}
) => {
  try {
    const useStream = await getModeStreamPreference(requestBody?.mode);
    if (useStream) {
      return await requestModelStream(requestBody, onChunkReceived, options);
    }

    const result = await requestModelNonStream(requestBody, options);
    if (onChunkReceived) {
      onChunkReceived(result.fullResponse);
    }
    return result;
  } catch (error) {
    const cleanedMessage = cleanErrorMessage(error?.message);
    throw new Error(cleanedMessage);
  }
};

/**
 * 使用 EventSource 处理流式响应的函数
 * @param {string} endpoint - API端点
 * @param {Object} requestData - 请求数据
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @param {function} onError - 错误处理回调
 * @returns {Promise<string>} 完整的响应文本
 */
export const handleStreamWithEventSource = (
  endpoint,
  requestData,
  onChunkReceived,
  onError
) => {
  return new Promise((resolve, reject) => {
    // 确保请求参数是查询字符串的一部分
    const params = new URLSearchParams(requestData).toString();
    const url = `${endpoint}?${params}`;

    // 创建 EventSource 连接
    const eventSource = new EventSource(url);
    let fullResponse = "";

    // 监听消息
    eventSource.onmessage = (event) => {
      try {
        if (event.data === "[DONE]") {
          eventSource.close();
          resolve(fullResponse);
          return;
        }

        const parsed = JSON.parse(event.data);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) {
          fullResponse += content;
          if (onChunkReceived) {
            onChunkReceived(content);
          }
        }
      } catch (error) {
        Logger.warn("解析流式响应JSON时出错", {
          error: error.message,
          data: event.data,
        });
      }
    };

    // 监听错误
    eventSource.onerror = (error) => {
      eventSource.close();
      const errorMessage = "流式响应连接出错";
      Logger.error(errorMessage, error);
      if (onError) {
        onError(errorMessage);
      }
      reject(new Error(errorMessage));
    };
  });
};

/**
 * 将图片文件转换为base64格式
 * @param {File} file - 图片文件
 * @returns {Promise<string>} - base64编码的图片数据
 */
export const convertImageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("没有提供文件"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * 请求队列类
 * 用于管理API请求队列和限流
 */
export class RequestQueueManager {
  constructor() {
    this.processing = false;
    this.queue = [];
    this.activeRequests = 0;
    this.lastRequestTime = 0;
  }

  /**
   * 添加请求到队列
   * @param {Function} requestFunction 请求函数
   * @returns {Promise} 请求Promise
   */
  async enqueue(requestFunction) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFunction, resolve, reject });
      this.processQueue();
    });
  }

  /**
   * 处理请求队列
   */
  async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (
      this.queue.length > 0 &&
      this.activeRequests < RATE_LIMIT_CONFIG.maxConcurrentRequests
    ) {
      const { requestFunction, resolve, reject } = this.queue.shift();

      // 检查请求间隔
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      if (timeSinceLastRequest < RATE_LIMIT_CONFIG.minRequestInterval) {
        const waitTime =
          RATE_LIMIT_CONFIG.minRequestInterval - timeSinceLastRequest;
        Logger.debug(`等待请求间隔，延迟${waitTime}ms`);
        await delay(waitTime);
      }

      // 执行请求
      this.activeRequests++;
      this.lastRequestTime = Date.now();

      requestFunction()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          this.activeRequests--;
          // 继续处理队列
          setTimeout(() => this.processQueue(), 100);
        });
    }

    this.processing = false;
  }
}

// 创建一个全局请求队列管理器实例
export const requestManager = new RequestQueueManager();

export default {
  isDevelopment,
  delay,
  calculateRetryDelay,
  getCurrentUser,
  saveCurrentUser,
  fetchWithRetry,
  handleStreamResponse,
  handleStreamWithEventSource,
  convertImageToBase64,
  RequestQueueManager,
  requestManager,
  extractModelResponseText,
  requestModelNonStream,
  getModeStreamPreference,
  requestModelStream,
  requestModelByModeConfig,
  cleanErrorMessage,
};
